package com.swetdot.swetgram.ui.auth

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.auth.AuthRepository
import com.swetdot.swetgram.data.auth.SessionManager
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Drives the auth screens.
 *
 * Registration does NOT go straight to the app: the contract requires a verified email before
 * a user can send anything, so landing them in an empty chat list that rejects their first
 * message would be a worse experience than asking for the code up front.
 *
 * EVERY `catch` HERE TAKES `Throwable`, NOT `ApiError`, AND EVERY ONE HAS A `finally`.
 *
 * `ApiClient` decodes a response body OUTSIDE its try/catch ladder, so a body this build cannot
 * parse surfaces as a raw `SerializationException` rather than an `ApiError`; and `AuthRepository`
 * does non-network work after the call returns -- `session.adopt(response)` on login and register,
 * `session.markEmailVerified()` on verification -- which touches platform credential storage and
 * can throw anything. Nothing in the app installs a `CoroutineExceptionHandler`, so an escape from
 * `viewModelScope` had no handler at all: the throw simply left `busy = true` behind it, forever.
 *
 * That is not a cosmetic hang. SDS §25.1 asks a loading state to "prevent duplicate actions", and
 * a `busy` that never clears satisfies it absolutely -- nothing on the screen can be retried
 * without killing the app. The worst shape is [verifyEmail]: the server HAS verified the address,
 * the local flag is not set, the gate never opens, and the single-use code the user typed is
 * spent. `ProfileViewModel` and `ChatViewModel` already carry this exact edit; this file is the
 * front door and was the last one without it.
 */
class AuthViewModel(
    private val auth: AuthRepository,
    private val session: SessionManager,
) : ViewModel() {

    private val _state = MutableStateFlow(AuthUiState())
    val state: StateFlow<AuthUiState> = _state.asStateFlow()

    /**
     * Password recovery, kept in its own flow rather than folded into [state].
     *
     * The two are genuinely independent: recovery has a position in a wizard that sign-in and
     * registration do not, and sharing one `AuthUiState` would mean a failed sign-in could blank
     * the step a user is halfway through.
     */
    private val _recovery = MutableStateFlow(RecoveryState())
    val recovery: StateFlow<RecoveryState> = _recovery.asStateFlow()

    fun signIn(identifier: String, password: String) = run {
        _state.value = AuthUiState(busy = true)
        viewModelScope.launch {
            try {
                auth.login(identifier, password)
                _state.value = AuthUiState()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = authStateFor(error)
            } finally {
                // Unconditional, and it has to be: the success and failure branches above each
                // assign a whole fresh AuthUiState, so this only ever fires for a path that
                // assigned neither -- which is exactly the one that used to leave the button
                // spinning with nothing on screen to explain it.
                _state.value = _state.value.copy(busy = false)
            }
        }
    }

    fun register(form: RegisterForm) {
        _state.value = AuthUiState(busy = true)
        viewModelScope.launch {
            try {
                auth.register(
                    displayName = form.displayName,
                    username = form.username,
                    email = form.email,
                    password = form.password,
                    acceptedLegal = form.acceptedLegal,
                    ageConfirmed = form.ageConfirmed,
                )
                _state.value = AuthUiState()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = authStateFor(error)
            } finally {
                _state.value = _state.value.copy(busy = false)
            }
        }
    }

    fun verifyEmail(code: String) {
        _state.value = AuthUiState(busy = true)
        viewModelScope.launch {
            try {
                auth.verifyEmail(code)
                // The 204 IS the confirmation; flipping the stored flag is what moves the
                // gate on to the app.
                session.markEmailVerified()
                _state.value = AuthUiState()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // A rejected code is a field problem, not a server failure -- say so plainly
                // rather than showing "something went wrong".
                _state.value = if (error is ApiError.Validation) {
                    AuthUiState(banner = AuthBanner.CodeRejected)
                } else {
                    authStateFor(error)
                }
            } finally {
                // The `markEmailVerified` case is the reason this exists. It runs AFTER the server
                // has accepted the code, so a failure there leaves an account that is verified on
                // the server and a client that will not admit it. A banner and a usable button are
                // the least the screen owes: the user can sign out and back in, which re-reads the
                // flag. Silently spinning gave them nothing.
                _state.value = _state.value.copy(busy = false)
            }
        }
    }

    fun resendVerification() {
        _state.value = AuthUiState(busy = true)
        viewModelScope.launch {
            try {
                auth.resendVerification()
                _state.value = AuthUiState()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = authStateFor(error)
            } finally {
                _state.value = _state.value.copy(busy = false)
            }
        }
    }

    /** Escape from the verification screen when the address was mistyped. */
    fun signOut() {
        viewModelScope.launch { auth.logout() }
    }

    fun clearErrors() {
        _state.value = AuthUiState()
    }

    // ------------------------------------------------------------------------ password recovery

    /** Opens the flow from scratch, discarding whatever a previous attempt left behind. */
    fun startRecovery() {
        _recovery.value = RecoveryState()
    }

    /** Back to the address step, for a user who realises they typed the wrong one. */
    fun restartRecovery() {
        _recovery.value = _recovery.value.restart()
    }

    /**
     * Step one, and the resend on step two -- deliberately the same call, because the contract
     * has no separate resend for password reset the way it does for verification. Sending it
     * again simply issues another code.
     *
     * The success path does NOT inspect the response, and there is nothing in it to inspect: the
     * server answers `202` with an empty body for every address. See [RecoveryState].
     */
    fun requestPasswordReset(email: String) {
        _recovery.value = _recovery.value.working()
        viewModelScope.launch {
            try {
                auth.forgotPassword(email)
                _recovery.value = _recovery.value.codeSent(email)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _recovery.value = _recovery.value.failedWith(error)
            } finally {
                _recovery.value = _recovery.value.copy(
                    form = _recovery.value.form.copy(busy = false),
                )
            }
        }
    }

    /**
     * Step two. On success the flow is done, and the user is NOT signed in: `resetPassword`
     * answers 204 with no session, and the server revokes every existing session as part of the
     * reset -- which is the point of it, since the whole reason for recovering a password is that
     * someone else may hold the old one. So the caller returns to sign-in.
     */
    fun resetPassword(code: String, newPassword: String) {
        _recovery.value = _recovery.value.working()
        viewModelScope.launch {
            try {
                auth.resetPassword(code, newPassword)
                _recovery.value = _recovery.value.finished()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _recovery.value = _recovery.value.failedWith(error)
            } finally {
                _recovery.value = _recovery.value.copy(
                    form = _recovery.value.form.copy(busy = false),
                )
            }
        }
    }
}

/**
 * An [AuthUiState] for ANY failure, not just an [ApiError].
 *
 * SDS §18.2 forbids showing the raw error, and there is nothing useful to say about a body that
 * would not parse or a keychain that would not write beyond §17.2's own error copy -- which is
 * what [AuthBanner.Unexpected] renders. The point is not the sentence; it is that there IS one,
 * where before the exception left the scope and the screen said nothing at all.
 *
 * A free function rather than an extension, so it cannot be confused at a call site with
 * `ApiError.toAuthState`, which stays the authority on every failure the contract describes.
 */
internal fun authStateFor(error: Throwable): AuthUiState =
    if (error is ApiError) error.toAuthState() else AuthUiState(banner = AuthBanner.Unexpected)

/**
 * [RecoveryState.failed] widened the same way, and it keeps that method's rule: a failure never
 * moves the step. Sending the user forward on an exception would leave them on a code screen
 * waiting for an email that was never scheduled.
 */
internal fun RecoveryState.failedWith(error: Throwable): RecoveryState =
    if (error is ApiError) failed(error) else copy(form = AuthUiState(banner = AuthBanner.Unexpected))
